// A TATTOO CONVENTION'S ARTIST FIELD: a search over the show's roster (convention.ts says why and holds every decision).
//
// One box and, under it while the client types, the names that match as buttons a thumb can hit. A tap fills the box with
// the list's own spelling; a name the list does not have stays exactly as typed, and the line under the box says it will
// be used that way. The box is the field the form posts (`nameOfArtist`); the parent keeps the choice beside it, so the
// body is built by the same withArtistPicked a shop's picker uses.

import React, { useId, useState } from "react";
import type { ArtistOption } from "./artists";
import { SOMEONE_ELSE } from "./artists";
import { choiceForTyped, conventionCopy, matchArtists } from "./convention";
import type { Lang } from "./i18n";

type Props = {
  options: ArtistOption[];
  /** what is in the box: the form's own `nameOfArtist` */
  value: string;
  /** the parent's choice beside it: a name off the list, "Someone else", or "" */
  choice: string;
  service: "tattoo" | "piercing" | "cosmetic" | "";
  lang: Lang;
  maxLength: number;
  /** TW-0104 — the license number beside a name where the state's rule wants it on the record (Oregon) */
  showLicense: boolean;
  licenseWord: string;
  onChange: (text: string, choice: string) => void;
};

export default function ArtistSearch({ options, value, choice, service, lang, maxLength, showLicense, licenseWord, onChange }: Props) {
  const copy = conventionCopy(service, lang);
  const listId = useId();
  const [open, setOpen] = useState(false);
  // the list shows while the client is typing, and stays shut once the box holds a name off it exactly: nothing left to pick
  const picked = Boolean(choice) && choice !== SOMEONE_ELSE && choice === value;
  const shown = open && !picked ? matchArtists(options, value) : [];
  return (
    <div className="flex flex-col w-full" data-artist-search="">
      <input
        className="w-full border-2 p-2"
        type="text"
        name="nameOfArtist"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown.length > 0}
        aria-controls={listId}
        placeholder={copy.placeholder}
        maxLength={maxLength}
        value={value}
        autoComplete="off"
        required
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          onChange(e.target.value, choiceForTyped(options, e.target.value));
        }}
      />
      {shown.length > 0 && (
        <ul id={listId} role="listbox" aria-label={copy.listLabel} className="border-2 border-t-0 bg-white">
          {shown.map((artist) => (
            <li key={artist.id || artist.name} role="option" aria-selected={artist.name === choice}>
              <button
                type="button"
                className="w-full text-left p-3 border-b"
                onClick={() => {
                  setOpen(false);
                  onChange(artist.name, artist.name);
                }}
              >
                {artist.name}
                {showLicense && artist.licenseNumber ? ` · ${licenseWord} ${artist.licenseNumber}` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="ps-1">
        <span className="text-sm text-gray-500">{choice === SOMEONE_ELSE && value.trim() ? copy.notListed : copy.hint}</span>
      </div>
    </div>
  );
}
